import React, {useEffect, useState} from 'react';
import {Dispatch, bindActionCreators} from 'redux';
import {FormattedMessage, injectIntl} from 'react-intl';
import {connect} from 'react-redux';
import {toast} from 'react-toastify';
import {translatedText} from '../../components/TranslatedText';

import {RootState} from '../../reducers';
import * as actions from '../../actions/generalActions';

import './Login.scss'
import {Input} from '../../components/Input/Input';
import {Button, EButtonType} from '../../components/Button';

import LoginBg from "../../assets/img/login-bg.png";
import LoginLogo from "../../assets/icons/MIKA_logo-01.svg";
import {withRouter} from 'react-router';
import Spinner from '../../components/Spinner';
import MikaTextInput from '../../components/Input/MikaTextInput/MikaTextInput';
import * as Yup from 'yup';
import FormikFormWrapper from "../../components/FormikFormWrapper/FormikFormWrapper";
import MikaPasswordInput from "../../components/Input/MikaPasswordInput/MikaPasswordInput";
import {LOGIN_VALIDATION} from "../../utils/validationConfigs";
import {forgotPassword} from "../../actions/generalActions";
import LocaleSwitcher from "../../components/LocaleSwitcher/LocaleSwitcher";

interface Props {
    intl: any;
    actions: any;
    setLoading: boolean;
    user: any;
    history: any;
    loading: boolean;
    permissions: any;
    buttonLoader: boolean
}

interface State {
    isLoginForm: boolean;
    rememberMe: boolean;
    email: string;
    username: string;
    password: string;
}
interface LoginCredentials {
    username: string;
    password: string;
}

const Login = (props: Props) => {
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [rememberMe, setRememberMe] = useState(false)
    const [isLoginForm, setIsLoginForm] = useState(true)
    const fm = (id: string, defaultMessage: string) => props.intl.formatMessage({ id, defaultMessage });

    // If we landed here because the session-expired interceptor kicked us out
    // (e.g. a super-admin suspended our tenant mid-session), surface the reason
    // to the user as an info toast. The URL param is stripped so a manual page
    // refresh doesn't re-toast the same message forever.
    useEffect(() => {
        const search = new URLSearchParams(props.history.location.search);
        const reason = search.get('reason');
        if (reason === 'session_ended') {
            toast.info(translatedText({
                id: 'errors.auth.session_ended',
                defaultMessage: 'Sesioni juaj ka perfunduar. Ju lutem kycuni perseri.',
            }), { autoClose: 6000 });
            search.delete('reason');
            const newSearch = search.toString();
            const newUrl = props.history.location.pathname + (newSearch ? `?${newSearch}` : '');
            props.history.replace(newUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        localStorage?.removeItem('mikaSelectedLanguage')
        const isAuthenticated = props.user.isAuthenticated
        if (isAuthenticated) {
            props.actions.isLogin(true);
            props.actions.isAuthenticated(true);
            props.actions.setLoading(true);
            props.actions.getUserPermissionsAndResponsibilities().then((res)=>{
                let permissions = res?.data?.permissions

                if (permissions.superAdmin) {
                    // Analitika is temporarily hidden, so land super admins on
                    // Contact like fullPrivileges instead of the hidden module.
                    props.history.push('/dashboard/contact');
                } else if (permissions.fullPrivileges) {
                    props.history.push('/dashboard/contact');
                } else {
                    const firstPerm = Object.keys(permissions)[0];
                    const moduleName = firstPerm.split('.')[0];
                    const route = moduleName === 'production' ? '/dashboard/production/board' : `/dashboard/${moduleName}`;
                    props.history.push(route);
                }
            })
        }
    }, [props.user.isAuthenticated])

    function validationScheme() {
        return Yup.object().shape({
            username: LOGIN_VALIDATION,
            password: LOGIN_VALIDATION,
        });
    }

    function validationSchemeForgotPassword() {
        return Yup.object().shape({
            email: LOGIN_VALIDATION,
        });
    }

    function login(values: LoginCredentials) {
        const loginPayload = {
            username: values?.username,
            password: values?.password
        }
        props.actions.login(loginPayload)
    }

    const initialValues = {
        username: '',
        password: ''
    };

    const initialValuesForgotPassword = {
        email: ''
    };

    function loginForm() {
        return (
            <>
                {props.loading ? <Spinner isTransparent /> :
                    <div>
                        <p className="login-text medium-text">
                            <FormattedMessage id="auth.login.tagline"
                                              defaultMessage="Welcome! Please sign in to your account."/>
                        </p>
                        <FormikFormWrapper initialValues={initialValues} validationScheme={validationScheme()}
                                           onSubmit={login}>
                            <MikaTextInput name="username" flat placeholder={fm("auth.login.usernamePlaceholder", "Username")}/>
                            <MikaPasswordInput name="password" flat placeholder={fm("auth.login.passwordPlaceholder", "Password")}/>
                            <span className="d-flex justify-content-between">
                                <Input
                                    type="checkbox"
                                    inputWrapperClass="checkbox-input toggle-checkbox"
                                    name="rememberMe"
                                    value={rememberMe}
                                    checked={rememberMe}
                                    onChange={(name, value) => setRememberMe(value)}
                                    checkboxLabel={fm("auth.login.rememberMe", "Remember me")}
                                />
                                <Button
                                    isLoading={props?.buttonLoader}
                                    isDisabled={props?.buttonLoader}
                                    onClick={() => {
                                        setIsLoginForm(false)
                                    }}
                                    className="forgot-password"
                                    buttonType={EButtonType.BasicLink}>
                                    <FormattedMessage id="auth.login.forgotPasswordLink"
                                                      defaultMessage="Forgot your password?"/>
                                </Button>
                            </span>
                            <div className="col text-center">
                                <Button
                                    isLoading={props?.buttonLoader}
                                    isDisabled={props?.buttonLoader}
                                    submit
                                    className="m-auto mt-5"
                                    buttonType={EButtonType.PurpleButton}>
                                    <FormattedMessage id="auth.login.submit" defaultMessage="Log in"/>
                                </Button>
                            </div>
                        </FormikFormWrapper>
                    </div>}
            </>
        )
    }

    function sendEmail(values){
        let payload = {
            email: values.email
        }
        props.actions.forgotPassword(payload)
    }

    function changePasswordForm() {
        return (
            <>
            {props.loading ? <Spinner isTransparent /> :
            <div>
                <FormikFormWrapper onSubmit={sendEmail} initialValues={initialValuesForgotPassword} validationScheme={validationSchemeForgotPassword}>
                    <p className="login-text medium-text">
                        <FormattedMessage id="auth.forgot.tagline"
                                        defaultMessage="Enter your email and we'll send you a link to reset your password."/>
                    </p>
                    <MikaTextInput name="email" flat placeholder={fm("auth.forgot.emailPlaceholder", "Email")}/>
                    <div className="col text-center">
                        <Button
                            isLoading={props?.buttonLoader}
                            isDisabled={props?.buttonLoader}
                            submit={true}
                            className="m-auto mt-4"
                            buttonType={EButtonType.PurpleButton}>
                            <FormattedMessage id="auth.forgot.submit" defaultMessage="Send request"/>
                        </Button>
                    </div>
                </FormikFormWrapper>
            </div>
            }
            </>
        )
    }

    return (
        <div className="container-fluid height111vh">
            <div className="height111vh loginContainer">
                <div className="left-side">
                    <img src={LoginBg} className="login-bg" alt="login bg"/>
                </div>
                <div className="right-side d-flex justify-content-center align-items-center" style={{ position: 'relative' }}>
                    <div style={{ position: 'absolute', top: 20, right: 20, zIndex: 10 }}>
                        <LocaleSwitcher />
                    </div>
                    <div className="d-flex flex-column">
                        <img className="login-logo" onClick={() => {
                            setIsLoginForm(true)
                        }} src={LoginLogo} alt="login logo"/>
                        {isLoginForm ? loginForm() : changePasswordForm()}
                    </div>
                    <p className="mini-text terms-policy">
                        <FormattedMessage id="auth.login.termsLink"
                                          defaultMessage="Terms of use · Privacy policy"/>
                    </p>
                </div>
            </div>
        </div>
    )
}

function mapGlobalStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        ...state.app,
        user: state.user
    }
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
    }
}

export default withRouter(connect(mapGlobalStateToProps, mapDispatchToProps)(injectIntl(Login as any)));
